import { useId, useState } from 'react';
import { Box, Typography, CircularProgress, Button, Autocomplete, TextField } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Modal from 'shared/Modal';
import { ModalPropsToOverride } from 'components/DynamicModalProvider/types';
import { useSelector } from 'react-redux';
import { selectAuthAccount } from 'AuthModule/selectors';
import { selectTeamHash } from 'TeamModule/selectors';
import { useCreateDraftMutation } from 'IntegrationModule/api/queries/useCreateDraftMutation';
import {
  useFilterIntegrationsByTenantQuery,
  filterIntegrationsByTenantQueryKey,
  TenantServiceOption,
} from 'IntegrationModule/api/queries/useFilterIntegrationsByTenantQuery';
import { AddServiceToRealmSection } from './AddServiceToRealmSection';

interface CreateDraftModalProps extends ModalPropsToOverride {
  teamId: number;
  tenant?: string;
  realmId?: string;
}

const CreateDraftModal = ({ isOpen, toggle, teamId, tenant, realmId }: CreateDraftModalProps) => {
  const account = useSelector(selectAuthAccount);
  const teamHash = useSelector(selectTeamHash);
  const teamName = teamHash[teamId]?.name || '';
  const { serviceOptions, isLoadingServices, isErrorServices } = useFilterIntegrationsByTenantQuery(
    {
      tenant,
      realmId,
    },
  );

  const [selectedService, setSelectedService] = useState<TenantServiceOption | null>(null);
  const [isAddServiceExpanded, setIsAddServiceExpanded] = useState(false);
  const queryClient = useQueryClient();
  // Lets the submit button live outside the <form> element (the collapsible
  // Add Service To Realm section between them embeds its own form, and forms
  // must not nest). Per-instance so stacked modals don't collide.
  const formId = useId();

  const { createDraftAsync, isCreatingDraft } = useCreateDraftMutation();

  const handleServiceAddedToRealm = () => {
    // Refetch the tenant services so the just-added service shows up in the
    // dropdown (and loses its auto-add hint). Scoped to exactly this query —
    // adding a service to the realm doesn't affect other integration lists.
    queryClient.invalidateQueries({
      queryKey: filterIntegrationsByTenantQueryKey({ tenant, realmId }),
    });
    setIsAddServiceExpanded(false);
  };

  const galaxyUserId = account?.id?.toString() || '0';
  const galaxyUserEmail = account?.email || '';

  const serviceName = selectedService?.serviceName || '';

  // The selected option object goes stale when the services list refetches
  // (onRealm flips once the service is added via the section below) — read
  // the fresh copy for anything derived from onRealm.
  const selectedOption = selectedService
    ? (serviceOptions.find((option) => option.serviceName === selectedService.serviceName) ??
      selectedService)
    : null;

  const isFormValid = serviceName && realmId && galaxyUserId;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isFormValid || isCreatingDraft) return;

    try {
      await createDraftAsync({
        mosaicTeamId: teamId,
        mosaicTeamName: teamName,
        serviceName,
        realmId: realmId!,
        bakIntegration: false,
        galaxyUserId,
        galaxyUserEmail,
        tenant,
      });

      toggle();
    } catch {
      // Error notifications are shown by the mutation hooks.
      // Modal stays open so the user can retry or fix inputs.
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={isCreatingDraft ? undefined : toggle}
      modalWidth={600}
      // Expanded, the content outgrows the default maxHeight (600) — pin the
      // height so the modal scrolls instead of spilling past its border.
      modalHeight={isAddServiceExpanded ? 600 : undefined}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Create Draft Integration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a draft integration for Team {teamId}.
        </Typography>

        <form id={formId} onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              options={serviceOptions}
              getOptionLabel={(option) => option.label}
              loading={isLoadingServices}
              value={selectedService}
              onChange={(_, newValue) => setSelectedService(newValue)}
              disabled={isCreatingDraft}
              slotProps={{
                paper: {
                  // The popup floats over page content and the dark variant's
                  // background.paper is deliberately translucent, so pin an
                  // opaque surface there. Pinning that dark navy in *every*
                  // mode is what left near-black option text on it (~1.6:1) in
                  // the light themes.
                  sx: (theme) => ({
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#233044' : theme.palette.background.paper,
                  }),
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Service Name" required size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.serviceName === value.serviceName}
            />
            {/* Shown even when cached options remain: a failed background
                refetch (e.g. right after adding a service below) keeps the
                previous list, which would silently omit the new service. */}
            {!isLoadingServices && isErrorServices && (
              <Typography variant="caption" color="error">
                {serviceOptions.length === 0
                  ? "Couldn't load services for this tenant — try again later."
                  : "Couldn't refresh services for this tenant — the list may be out of date. Try again later."}
              </Typography>
            )}
            {!isLoadingServices && !isErrorServices && serviceOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No services are available for this tenant.
              </Typography>
            )}
            {selectedOption && !selectedOption.onRealm && (
              <Typography variant="caption" color="text.secondary">
                This service isn&apos;t on the realm yet — it will be added automatically, so
                creating the draft may take a little longer.
              </Typography>
            )}
          </Box>
        </form>

        {realmId && (
          <AddServiceToRealmSection
            realmId={realmId}
            isExpanded={isAddServiceExpanded}
            onToggle={() => setIsAddServiceExpanded((prev) => !prev)}
            onServiceAdded={handleServiceAddedToRealm}
          />
        )}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
          <Button variant="outlined" onClick={toggle} disabled={isCreatingDraft}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="contained"
            disabled={!isFormValid || isCreatingDraft}
          >
            {isCreatingDraft ? <CircularProgress size={20} color="inherit" /> : 'Create Draft'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateDraftModal;
